import {
  FlagIcon,
  LogOutIcon,
  PencilIcon,
  ShieldMinusIcon,
  ShieldPlusIcon,
  UserMinusIcon,
} from 'lucide-react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import React, { useEffect, useState } from 'react';

import { PlaceholderMemberService } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type { PlaceholderClaimDto } from '@splitfiesta/shared';
import { Alert, Avatar, Button, Card, Input } from '@splitfiesta/ui';

import { withAuth } from '@/components/auth';
import { GroupPageLayout } from '@/components/groups/GroupPageLayout';
import { InvitationLink } from '@/components/groups/InvitationLink';
import { AddPlaceholderMemberForm } from '@/components/members/AddPlaceholderMemberForm';
import { MemberActionRow } from '@/components/members/MemberActionRow';
import { MemberListItem } from '@/components/members/MemberListItem';
import { PlaceholderBadge } from '@/components/members/PlaceholderBadge';
import { PlaceholderClaimBlock } from '@/components/members/PlaceholderClaimBlock';
import { MembersContentSkeleton } from '@/components/skeleton';

import { useGroupClaimsQuery } from '@/hooks/queries/useGroups';
import { useGroupMembers } from '@/hooks/useGroupMembers';
import { useLeaveGroup } from '@/hooks/useLeaveGroup';
import { useReportContent } from '@/hooks/useReportContent';

import { resolveUserName } from '@/lib/displayName';

/**
 * Brand edge on a failed merge a decider can retry. Drawn as an overlay
 * rather than a left border so the row's avatar keeps its exact alignment
 * with every other member row.
 */
const CLAIM_ACCENT =
  'relative before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-primary-500';

interface OrphanClaimRowProps {
  claim: PlaceholderClaimDto;
  /** The merge's progress line, or the retry for a decider. */
  claimBlock?: React.ReactNode;
  className?: string;
}

/**
 * A claim whose placeholder row is already gone (a failed merge removes the
 * member before the claim is resolved). Rendered after the member list so the
 * owner can still act on it.
 */
function OrphanClaimRow({
  claim,
  claimBlock,
  className = '',
}: OrphanClaimRowProps) {
  const { t } = useTranslation();

  return (
    <div
      className={`flex items-center py-3 sm:py-4 ${className}`}
      data-testid={`orphan-claim-${claim.placeholderId}`}
    >
      <div className="flex items-center min-w-0 flex-1">
        <Avatar
          name={claim.placeholderName}
          size="md"
          className="mr-3 shrink-0"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
            <p className="text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100 truncate">
              {claim.placeholderName}
            </p>
            <PlaceholderBadge />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t('groups:placeholderMembers.mergingInto', {
              claimant: claim.claimantName,
            })}
          </p>
          {claimBlock && <div className="mt-1">{claimBlock}</div>}
        </div>
      </div>
    </div>
  );
}

function GroupMembersPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const reportContent = useReportContent();
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const arrivedToAddPlaceholder =
    (location.state as { addPlaceholder?: boolean } | null)?.addPlaceholder ===
    true;
  const [addPlaceholderOpen, setAddPlaceholderOpen] = useState(
    arrivedToAddPlaceholder
  );

  // Consume the one-shot router state so a refresh does not reopen the form.
  useEffect(() => {
    if (arrivedToAddPlaceholder) {
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: null,
      });
    }
  }, [arrivedToAddPlaceholder, location.pathname, location.search, navigate]);

  // Use the extracted group members hook
  const {
    // Data
    group,
    sortedBalances,

    // Loading states
    initialLoading,
    removing,
    promoting,
    renamingPlaceholder,
    removingPlaceholder,

    // Status
    error,
    success,
    placeholderError,
    isOnline,

    // Computed
    isGroupOwner,
    isCurrentUserAdmin,

    // Actions
    handleRemoveMember,
    handlePromoteToAdmin,
    handleDemoteToMember,
    handleRenamePlaceholder,
    handleRemovePlaceholder,

    // User context
    user,
  } = useGroupMembers({ groupId: id ?? '' });

  // Deciders-only endpoint, so plain members never fire the request.
  const { data: claims } = useGroupClaimsQuery(id ?? '', {
    enabled: PlaceholderMemberService.canDecideClaims(group?.userRole),
    refetchOnMount: 'always',
  });

  // Load required namespaces
  useNamespace('groups');
  useNamespace('members');

  // Leave group hook
  const { leaveGroup, isLeaving } = useLeaveGroup({
    groupId: id ?? '',
    groupName: group?.name || '',
  });

  // Early return after all hooks have been called
  if (!id) {
    return <Navigate to="/groups" replace />;
  }

  if (!user) {
    return null;
  }

  if (initialLoading) {
    return (
      <GroupPageLayout groupId={id}>
        <MembersContentSkeleton />
      </GroupPageLayout>
    );
  }

  // The layout reads the same group query, so a group that failed to load
  // never reaches the content — the layout renders the error instead.
  if (!group) {
    return <GroupPageLayout groupId={id}>{null}</GroupPageLayout>;
  }

  const offlineTitle = t('groups:placeholderMembers.offlineDisabled');
  const canAddPlaceholders = PlaceholderMemberService.canAdd(group.userRole);
  const orphanClaims = PlaceholderMemberService.orphanClaims(
    claims ?? [],
    sortedBalances
  );
  const viewerCannotClaim = PlaceholderMemberService.viewerCannotClaim(
    sortedBalances,
    user.id,
    group.viewerHasMergedClaim
  );

  return (
    <GroupPageLayout groupId={id}>
      <div className="space-y-6">
        {error && (
          <Alert
            type="error"
            message={
              typeof error === 'string' ? error : t(error.key, error.params)
            }
          />
        )}

        {success && (
          <Alert
            type="success"
            message={
              typeof success === 'string'
                ? success
                : t(success.key, success.params)
            }
          />
        )}

        {placeholderError && (
          <Alert
            type="error"
            message={placeholderError}
            data-testid="placeholder-error"
          />
        )}

        {/* One roster card: the entry actions read as rows of the same
            list the members are in (parity with the mobile Members tab) */}
        <Card
          data-testid="members-section"
          padding="none"
          className="overflow-hidden p-0"
        >
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {/* Invite Members - Any member can invite */}
            <InvitationLink
              groupId={id}
              groupName={group.name}
              userRole={group.userRole}
              onAddPlaceholder={() => setAddPlaceholderOpen(true)}
              presentation="row"
            />

            {/* Add someone who has no account (any member) */}
            {canAddPlaceholders && (
              <AddPlaceholderMemberForm
                groupId={id}
                open={addPlaceholderOpen}
                onOpenChange={setAddPlaceholderOpen}
                disabled={!isOnline}
                disabledReason={offlineTitle}
              />
            )}

            <h3
              className="px-6 py-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400"
              data-testid="current-members-title"
            >
              {t('groups:tabs.members')} · {sortedBalances.length}
            </h3>

            {sortedBalances && sortedBalances.length > 0 ? (
              sortedBalances.map(balance => {
                const memberId = balance.userId;
                const memberRole = balance.role;
                const isOwner = memberRole === 'owner';
                const isMemberAdmin =
                  memberRole === 'admin' || memberRole === 'owner';
                const memberData = {
                  id: memberId,
                  name: balance.userName || `Member ${memberId.slice(-4)}`,
                  profileImage: balance.profileImage,
                  joinedAt: balance.joinedAt || new Date().toISOString(),
                  isSupporter: !!balance.isSupporter,
                };
                // Resolved name for visible text (action titles/confirms).
                // memberData.name stays raw so Avatar can render the
                // deleted-user sentinel as '?'.
                const memberDisplayName = balance.userName
                  ? resolveUserName(balance.userName, t)
                  : `Member ${memberId.slice(-4)}`;

                const isPlaceholder =
                  PlaceholderMemberService.isPlaceholder(balance);
                const visibleClaim =
                  PlaceholderMemberService.visibleClaim(balance);
                const actions = PlaceholderMemberService.memberActions(
                  balance,
                  user.id,
                  group.userRole
                );

                // Can promote: current user is admin, target is not self, target is not already admin/owner
                const canPromote =
                  isCurrentUserAdmin && memberId !== user?.id && !isMemberAdmin;

                // Can demote: only the group owner can revoke admin
                // status. Admins promoting members is a grant; letting
                // admins demote other admins would let one admin undo
                // the owner's chosen lineup. The API enforces the same
                // rule (NotGroupOwnerError in updateMemberRole) — keep
                // the UI in step so users never see a 403 on click.
                const canDemote =
                  isGroupOwner &&
                  memberId !== user?.id &&
                  memberRole === 'admin';

                // Can remove: current user is admin/owner, target is not self, target is not owner
                const canRemove =
                  isCurrentUserAdmin && memberId !== user?.id && !isOwner;

                const rightContent =
                  renamingId === memberId ? (
                    <form
                      className="flex items-center gap-2"
                      data-testid={`rename-placeholder-form-${memberId}`}
                      onSubmit={async event => {
                        event.preventDefault();
                        const renamed = await handleRenamePlaceholder(
                          memberId,
                          renameValue
                        );
                        if (renamed) {
                          setRenamingId(null);
                        }
                      }}
                    >
                      <Input
                        aria-label={t('groups:placeholderMembers.nameLabel')}
                        value={renameValue}
                        onChange={event => setRenameValue(event.target.value)}
                        data-testid={`rename-placeholder-input-${memberId}`}
                        maxLength={50}
                        autoFocus
                      />
                      <Button
                        type="submit"
                        variant="primary"
                        size="sm"
                        disabled={!isOnline || renamingPlaceholder === memberId}
                        title={!isOnline ? offlineTitle : undefined}
                        data-testid={`rename-placeholder-submit-${memberId}`}
                      >
                        {t('groups:placeholderMembers.renameCta')}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setRenamingId(null)}
                        data-testid={`rename-placeholder-cancel-${memberId}`}
                      >
                        {t('groups:placeholderMembers.cancel')}
                      </Button>
                    </form>
                  ) : (
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      {/* Report Member — any member can report any other
                        member, including the owner (store compliance —
                        Apple 1.2 / Play UGC policy). A placeholder has
                        nobody behind it to report. */}
                      {actions.canReport && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            reportContent({
                              entityType: 'user',
                              entityId: memberId,
                              groupId: id,
                            })
                          }
                          className="text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 px-2 py-1.5 hover:bg-red-50 dark:hover:bg-red-900/10"
                          title={t('common:report.reportUser')}
                          data-testid={`report-member-${memberId}`}
                        >
                          <FlagIcon className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Make Admin Button */}
                      {canPromote && !isPlaceholder && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            handlePromoteToAdmin(memberId, memberDisplayName)
                          }
                          disabled={promoting === memberId}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 px-2 py-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/10"
                          title={t('members:actions.makeAdmin', {
                            name: memberDisplayName,
                          })}
                        >
                          {promoting === memberId ? (
                            <div className="loading-spinner w-4 h-4 dark:border-blue-400"></div>
                          ) : (
                            <>
                              <ShieldPlusIcon className="w-4 h-4" />
                              <span className="ml-1 text-xs">
                                {t('members:actions.makeAdminShort')}
                              </span>
                            </>
                          )}
                        </Button>
                      )}
                      {/* Demote to Member Button */}
                      {canDemote && !isPlaceholder && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            handleDemoteToMember(memberId, memberDisplayName)
                          }
                          disabled={promoting === memberId}
                          className="text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 px-2 py-1.5 hover:bg-amber-50 dark:hover:bg-amber-900/10"
                          title={t('members:actions.demoteToMember', {
                            name: memberDisplayName,
                          })}
                        >
                          {promoting === memberId ? (
                            <div className="loading-spinner w-4 h-4 dark:border-amber-400"></div>
                          ) : (
                            <>
                              <ShieldMinusIcon className="w-4 h-4" />
                              <span className="ml-1 text-xs">
                                {t('members:actions.demoteShort')}
                              </span>
                            </>
                          )}
                        </Button>
                      )}
                      {/* Remove Member Button */}
                      {canRemove && !isPlaceholder && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            handleRemoveMember(memberId, memberDisplayName)
                          }
                          disabled={removing === memberId}
                          className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 px-2 py-1.5 hover:bg-red-50 dark:hover:bg-red-900/10"
                          title={t('members:actions.removeMember', {
                            name: memberDisplayName,
                          })}
                        >
                          {removing === memberId ? (
                            <div className="loading-spinner w-4 h-4 dark:border-red-400"></div>
                          ) : (
                            <>
                              <UserMinusIcon className="w-4 h-4" />
                              <span className="ml-1 text-xs">
                                {t('members:actions.removeShort')}
                              </span>
                            </>
                          )}
                        </Button>
                      )}
                      {/* Rename Placeholder Button */}
                      {actions.canRename && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setRenameValue(memberDisplayName);
                            setRenamingId(memberId);
                          }}
                          disabled={!isOnline}
                          className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 px-2 py-1.5"
                          title={
                            !isOnline
                              ? offlineTitle
                              : t('groups:placeholderMembers.renameTitle')
                          }
                          data-testid={`rename-placeholder-${memberId}`}
                        >
                          <PencilIcon className="w-4 h-4" />
                          <span className="ml-1 text-xs">
                            {t('groups:placeholderMembers.renameTitle')}
                          </span>
                        </Button>
                      )}
                      {/* Remove Placeholder Button */}
                      {actions.canRemove && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            handleRemovePlaceholder(memberId, memberDisplayName)
                          }
                          disabled={
                            !isOnline || removingPlaceholder === memberId
                          }
                          className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 px-2 py-1.5 hover:bg-red-50 dark:hover:bg-red-900/10"
                          title={
                            !isOnline
                              ? offlineTitle
                              : t('groups:placeholderMembers.deleteTitle')
                          }
                          data-testid={`remove-placeholder-${memberId}`}
                        >
                          {removingPlaceholder === memberId ? (
                            <div className="loading-spinner w-4 h-4 dark:border-red-400"></div>
                          ) : (
                            <>
                              <UserMinusIcon className="w-4 h-4" />
                              <span className="ml-1 text-xs">
                                {t('groups:placeholderMembers.deleteCta')}
                              </span>
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  );

                const claimRow = visibleClaim
                  ? PlaceholderMemberService.claimRowPresentation({
                      claim: visibleClaim,
                      viewerId: user.id,
                      userRole: group.userRole,
                    })
                  : null;

                // Asked here rather than inside the block so a row with
                // nothing to say keeps its single-line layout.
                const claimState = isPlaceholder
                  ? PlaceholderMemberService.claimBlockState({
                      claim: visibleClaim,
                      viewerId: user.id,
                      userRole: group.userRole,
                      viewerCannotClaim,
                    })
                  : 'none';

                return (
                  <MemberListItem
                    key={memberId}
                    member={memberData}
                    isCurrentUser={memberId === user?.id}
                    isOwner={isOwner}
                    isAdmin={isMemberAdmin}
                    badge={isPlaceholder ? <PlaceholderBadge /> : undefined}
                    secondary={
                      claimState !== 'none' ? (
                        <PlaceholderClaimBlock
                          groupId={id}
                          placeholderId={memberId}
                          placeholderName={memberDisplayName}
                          groupName={group.name}
                          claim={visibleClaim}
                          viewerId={user.id}
                          userRole={group.userRole}
                          isOnline={isOnline}
                          viewerCannotClaim={viewerCannotClaim}
                        />
                      ) : undefined
                    }
                    rightContent={rightContent}
                    className={`px-6 ${claimRow?.canRetry ? CLAIM_ACCENT : ''}`}
                  />
                );
              })
            ) : (
              <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                {t('members:noMembers')}
              </div>
            )}
            {orphanClaims.map(claim => {
              const summary = PlaceholderMemberService.toClaimSummary(claim);
              const orphanRow = summary
                ? PlaceholderMemberService.claimRowPresentation({
                    claim: summary,
                    viewerId: user.id,
                    userRole: group.userRole,
                  })
                : null;
              return (
                <OrphanClaimRow
                  key={`${claim.placeholderId}-${claim.claimantId}`}
                  claim={claim}
                  claimBlock={
                    summary ? (
                      <PlaceholderClaimBlock
                        groupId={id}
                        placeholderId={claim.placeholderId}
                        placeholderName={claim.placeholderName}
                        groupName={group.name}
                        claim={summary}
                        viewerId={user.id}
                        userRole={group.userRole}
                        isOnline={isOnline}
                      />
                    ) : undefined
                  }
                  className={`px-6 ${orphanRow?.canRetry ? CLAIM_ACCENT : ''}`}
                />
              );
            })}

            {/* Leave Group - Non-owners can leave, from the roster itself */}
            {!isGroupOwner && (
              <MemberActionRow
                icon={LogOutIcon}
                tone="destructive"
                label={t('groups:leave.action')}
                description={t('members:leaveGroup.description')}
                onClick={leaveGroup}
                disabled={isLeaving}
                loading={isLeaving}
                testId="leave-group-button-members-page"
              />
            )}
          </div>
        </Card>

        {/* Report the group itself (name/photo) — reachable by every
            member (store compliance — Apple 1.2 / Play UGC policy) */}
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              id &&
              reportContent({
                entityType: 'group',
                entityId: id,
                groupId: id,
              })
            }
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
            data-testid="report-group-button"
          >
            <FlagIcon className="w-3.5 h-3.5 mr-1" />
            {t('common:report.reportGroup')}
          </Button>
        </div>
      </div>
    </GroupPageLayout>
  );
}

export default withAuth(GroupMembersPage);
